import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../constants";
import { audioManager } from "../systems/AudioManager";

export function addCoverImage(
  scene: Phaser.Scene,
  key: string,
  depth = 0,
): Phaser.GameObjects.Image {
  const image = scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, key);
  const scale = Math.max(GAME_WIDTH / image.width, GAME_HEIGHT / image.height);
  image.setScale(scale).setDepth(depth);
  return image;
}

export function fitLogo(
  scene: Phaser.Scene,
  x: number,
  y: number,
  maxWidth: number,
  maxHeight: number,
): Phaser.GameObjects.Image {
  const logo = scene.add.image(x, y, "logo-hibernation-run");
  const scale = Math.min(maxWidth / logo.width, maxHeight / logo.height, 1);
  logo.setScale(scale).setOrigin(0.5, 0.5);
  return logo;
}

export function addPanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
): Phaser.GameObjects.Container {
  const shadow = scene.add.rectangle(6, 8, width, height, 0x000000, 0.28);
  const children: Phaser.GameObjects.GameObject[] = [shadow];
  if (scene.textures.exists("panel-large")) {
    const maxInset = Math.max(48, Math.floor(Math.min(width, height) / 2) - 36);
    const inset = Math.min(118, maxInset);
    const panel = scene.add.nineslice(0, 0, "panel-large", undefined, width, height, inset, inset, inset, inset);
    children.push(panel);
  } else {
    const outer = scene.add.rectangle(0, 0, width, height, 0x5c3a1e, 1);
    outer.setStrokeStyle(5, 0x2a1a10);
    const inner = scene.add.rectangle(0, 0, width - 20, height - 20, 0xf3e6c8, 1);
    inner.setStrokeStyle(2, 0xc4a574);
    children.push(outer, inner);
  }
  const container = scene.add.container(x, y, children);
  container.setSize(width, height);
  return container;
}

export function dimScene(
  scene: Phaser.Scene,
  alpha = 0.58,
): Phaser.GameObjects.Rectangle {
  return scene.add
    .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x0d1c14, alpha)
    .setInteractive();
}

function makeToggle(
  scene: Phaser.Scene,
  x: number,
  y: number,
  size: number,
  depth: number,
  onKey: string,
  offKey: string,
  isOn: () => boolean,
  toggle: () => Promise<void> | void,
): Phaser.GameObjects.Image {
  const icon = scene.add.image(x, y, isOn() ? onKey : offKey);
  icon.setDisplaySize(size, size);
  icon.setDepth(depth);
  icon.setInteractive({ useHandCursor: true });
  const refresh = (): void => {
    icon.setTexture(isOn() ? onKey : offKey);
    icon.setAlpha(isOn() ? 1 : 0.78);
  };
  icon.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
    pointer.event.stopPropagation();
    void Promise.resolve(toggle()).then(refresh);
  });
  refresh();
  return icon;
}

export function addAudioToggles(
  scene: Phaser.Scene,
  x: number,
  y: number,
  size = 48,
  depth = 120,
): {
  music: Phaser.GameObjects.Image;
  sfx: Phaser.GameObjects.Image;
  fullscreen: Phaser.GameObjects.Image;
} {
  const gap = size + 14;
  const music = makeToggle(
    scene,
    x,
    y,
    size,
    depth,
    "icon-music-on",
    "icon-music-off",
    () => audioManager.musicEnabled,
    () => audioManager.toggleMusic(),
  );
  const sfx = makeToggle(
    scene,
    x + gap,
    y,
    size,
    depth,
    "icon-sound-on",
    "icon-sound-off",
    () => audioManager.sfxEnabled,
    () => audioManager.toggleSfx(),
  );
  const fullscreen = scene.add.image(x + gap * 2, y, "icon-fullscreen");
  fullscreen.setDisplaySize(size, size).setDepth(depth);
  fullscreen.setInteractive({ useHandCursor: true });
  const syncFullscreen = (): void => {
    fullscreen.setTint(scene.scale.isFullscreen ? 0xe8b84a : 0xffffff);
  };
  fullscreen.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
    pointer.event.stopPropagation();
    if (scene.scale.isFullscreen) {
      scene.scale.stopFullscreen();
    } else {
      scene.scale.startFullscreen();
    }
  });
  scene.scale.on(Phaser.Scale.Events.ENTER_FULLSCREEN, syncFullscreen);
  scene.scale.on(Phaser.Scale.Events.LEAVE_FULLSCREEN, syncFullscreen);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    scene.scale.off(Phaser.Scale.Events.ENTER_FULLSCREEN, syncFullscreen);
    scene.scale.off(Phaser.Scale.Events.LEAVE_FULLSCREEN, syncFullscreen);
  });
  syncFullscreen();
  return { music, sfx, fullscreen };
}
